import React, { useState, useEffect, useReducer } from 'react';
import { useParams } from 'react-router-dom';
import CuadroListaArbitro from './CuadroListaArbitro.js';
import CuadroEditableJugador from './CuadroEditableJugador.js';
import CuadroEditablePersona from './CuadroEditablePersona.js';
import CuadroEditableTecnico from './CuadroEditableTecnico.js';
import EstadisticasPersona from './EstadisticasPersona.js';
import LinksPersona from './LinksPersona.js';
import ParientesPersona from './ParientesPersona.js';
import TorneosPersona from './TorneosPersona.js';
import TrayectoriaClubes from './TrayectoriaClubes.js';
import TrayectoriaSeleccion from './TrayectoriaSeleccion.js';
import FichaSolapas from '../UI/FichaSolapas.js';
import GrillaResponsiva from '../UI/GrillaResponsiva.js';
import { Cuadro, NombreApellido } from '../funciones/FuncionesPersona.js';
import * as Data from '../data/DataPersonas.js';
import * as Reducer from '../reducers/ReducerDataAccess.js';
import styles from './DatosPersona.module.css';
import InformacionesPersona from './InformacionesPersona.js';
import BoletinesPersona from './BoletinesPersona.js';

const Solapas = {
  DATOS: 'Datos',
  JUGADOR: 'Como Jugador',
  TECNICO: 'Como Técnico',
  ARBITRO: 'Como Arbitro',
  TORNEOS: 'Torneos',
  ESTADISTICAS: 'Estadisticas',
};

const DatosVacio = {
  persona: null,
  jugador: null,
  tecnico: null,
  arbitro: null,
};

const DatosPersona = () => {
  const { id: idPersona } = useParams();
  const [datos, setDatos] = useState(DatosVacio);
  const [solapas, setSolapas] = useState([]);
  const [dataAccess, setDataAccess] = useReducer(Reducer.reducerDataAccess, Reducer.ReducerVacio);
  const esJugador = datos.persona && datos.persona.esJugador;
  const esTecnico = datos.persona && datos.persona.esTecnico;
  const esArbitro = datos.persona && datos.persona.esArbitro;

  useEffect(() => {
    if (datos.persona) {
      const listaSolapas = [
        { nombre: Solapas.DATOS, visible: true },
        { nombre: Solapas.TORNEOS, visible: true },
        { nombre: Solapas.JUGADOR, visible: datos.persona.esJugador },
        { nombre: Solapas.TECNICO, visible: datos.persona.esTecnico },
        { nombre: Solapas.ARBITRO, visible: datos.persona.esArbitro },
        { nombre: Solapas.ESTADISTICAS, visible: true },
      ];
      setSolapas(listaSolapas);
    }
  }, [datos.persona]);

  useEffect(() => {
    const cargarPersona = async () => {
      setDataAccess({ type: Reducer.Accion.CARGANDO, value: true });
      try {
        const datosPersona = await Data.GetPersona(idPersona);
        if (datosPersona && datosPersona !== Promise.resolve(datosPersona)) {
          setDatos(datosPersona);
        }
        setDataAccess({ type: Reducer.Accion.LIMPIAR, value: null });
      } catch (error) {
        setDataAccess({ type: Reducer.Accion.ERRORES, value: [error.message] });
      }
    };

    if (idPersona === null) {
      setDatos(DatosVacio);
      setDataAccess({ type: Reducer.Accion.LIMPIAR, value: null });
    } else {
      cargarPersona();
    }
  }, [idPersona]);

  const mostrarDatosSolapa = (solapa) => {
    switch (solapa) {
      case Solapas.DATOS: {
        const itemsIzq = [
          {
            id: 'nombres',
            componente: <CuadroEditablePersona cuadro={Cuadro.NOMBRES} persona={datos.persona} />,
          },
          {
            id: 'generales',
            componente: <CuadroEditablePersona cuadro={Cuadro.GENERALES} persona={datos.persona} />,
          },
          {
            id: 'nacimiento',
            componente: (
              <CuadroEditablePersona cuadro={Cuadro.NACIMIENTO} persona={datos.persona} />
            ),
          },
          {
            id: 'fallecimiento',
            componente: (
              <CuadroEditablePersona cuadro={Cuadro.FALLECIMIENTO} persona={datos.persona} />
            ),
          },
          { id: 'parientes', componente: <ParientesPersona persona={datos.persona} /> },
          { id: 'links', componente: <LinksPersona persona={datos.persona} /> },
          { id: 'informaciones', componente: <InformacionesPersona persona={datos.persona} /> },
        ];

        const itemsDer =
          esJugador || esTecnico
            ? [
                {
                  id: 'trayectoria_clubes',
                  componente: <TrayectoriaClubes persona={datos.persona} />,
                },
                {
                  id: 'trayectoria_seleccion',
                  componente: <TrayectoriaSeleccion persona={datos.persona} />,
                },
              ]
            : [];

        // Combinar items: los 7 de izquierda + los 2 de derecha = total 9
        const itemsCompletos = [...itemsIzq, ...itemsDer];

        return <GrillaResponsiva items={itemsCompletos} columnaSeparada={7} />;
      }
      case Solapas.TORNEOS: {
        return (
          <div className={`${styles.columnaSimple}`}>
            {(esJugador || esTecnico) && <TorneosPersona persona={datos.persona} />}
          </div>
        );
      }
      case Solapas.JUGADOR: {
        return (
          <div className={`${styles.columnaSimple}`}>
            {esJugador && datos.jugador && (
              <React.Fragment>
                <CuadroEditableJugador cuadro={Cuadro.JUGADOR} jugador={datos.jugador} />
                <BoletinesPersona persona={datos.persona} />
              </React.Fragment>
            )}
          </div>
        );
      }
      case Solapas.TECNICO: {
        return (
          <div className={`${styles.columnaSimple}`}>
            {esTecnico && datos.tecnico && (
              <CuadroEditableTecnico cuadro={Cuadro.TECNICO} tecnico={datos.tecnico} />
            )}
          </div>
        );
      }
      case Solapas.ARBITRO: {
        return (
          <div className={`${styles.columnaArbitro}`}>
            {esArbitro && datos.persona && (
              <CuadroListaArbitro cuadro={Cuadro.ARBITRO} persona={datos.persona} />
            )}
          </div>
        );
      }
      case Solapas.ESTADISTICAS: {
        return (
          <div className={`${styles.columnaSimple}`}>
            <EstadisticasPersona persona={datos.persona} />
          </div>
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <FichaSolapas
      item={datos.persona}
      titulo={NombreApellido(datos.persona)}
      solapas={solapas}
      solapaInicial={Solapas.DATOS}
      isCargando={dataAccess.cargando}
      errores={dataAccess.errores}
      mostrarDatosSolapa={mostrarDatosSolapa}
    />
  );
};

export default DatosPersona;
